import { FC, useState, useContext } from 'react'
import tw from 'tailwind-styled-components'
import { Collapse } from 'react-collapse'
import { CheckBox } from '../../../Inputs/CheckBox'
import { SvgIcon } from '../../../SvgIcon'
import LayersContext from '../../../../contexts/Layers'
import { LayerIdentifier } from './LayerIdentifier'
import MapChevron from '@foros-fe/assets/icons/map-chevron.svg'

type Item = {
  id: string
  title: string
}
type Props = Item & {
  items?: Item[]
}
type ListItemProps = Props & {
  child?: boolean
  toggleCollapse?(): void
  open?: boolean
}
type ElementProps = {
  $child?: boolean
}

const BoxContainer = tw.div`h-full flex items-center px-2 border-r border-gray-400`
const Line = tw.div<ElementProps>`
  border-b
  border-gray-400
  h-9
  flex
  ${({ $child }) => ($child ? 'bg-gray-200' : 'bg-white')}
`
const TitleContainer = tw.div<ElementProps & { $clickable: boolean }>`
  flex
  flex-1
  items-center
  pr-2
  ${({ $child }) => ($child ? 'pl-8' : 'pl-2')}
  ${({ $clickable }) => ($clickable ? 'cursor-pointer' : '')}
`
const IconContainer = tw.div<{ $open?: boolean }>`
  flex
  items-center
  justify-center
  min-w-[16px]
  min-h-[16px]
  transition-all
  duration-300
  ${({ $open }) => ($open ? 'rotate-90' : '')}
`

export const ListItem: FC<ListItemProps> = ({ title, id, items, child, toggleCollapse, open }) => {
  const { selected, toggleSelected } = useContext(LayersContext)
  const hasItems = !!items?.length
  const selectedCount = selected.filter((id) => items?.find((item) => item.id === id)).length
  const checked = hasItems ? selectedCount === items.length : selected.includes(id)

  return (
    <Line $child={child}>
      <BoxContainer>
        <CheckBox
          name={id}
          onChange={() => toggleSelected(hasItems ? items.map(({ id }) => id) : [id])}
          checked={checked}
          indeterminate={hasItems && !checked && selectedCount > 0}
        />
      </BoxContainer>
      <TitleContainer $child={child} $clickable={!!items?.length} onClick={toggleCollapse}>
        {items?.length ? (
          <IconContainer $open={open}>
            <SvgIcon src={MapChevron} size={12} />
          </IconContainer>
        ) : (
          <LayerIdentifier $id={id} />
        )}
        <span className='ml-2'>{title}</span>
      </TitleContainer>
    </Line>
  )
}

export const NestedListItem: FC<Props> = (props) => {
  const [open, setOpen] = useState(false)
  const { items } = props

  return (
    <>
      <ListItem {...props} toggleCollapse={() => setOpen(!open)} open={open} />
      {items?.length ? (
        <Collapse theme={{ collapse: 'duration-500' }} isOpened={open}>
          {items.map((item) => (
            <ListItem key={item.id} {...item} child />
          ))}
        </Collapse>
      ) : null}
    </>
  )
}
